import { createContext, use, useMemo, type PropsWithChildren } from "react";
import { useRouteLoaderData } from "react-router";
import { useShellActionFetcher } from "@/hooks/useShellActionFetcher";
import type { ShellBootstrapLoaderData } from "@/lib/bootstrap/runShellBootstrap";
import { postSessionLogout } from "@/services/auth.service";
import { getPublicRuntimeConfig } from "@/store/app/publicRuntimeConfigInstance";
import { ROOT_SHELL_ROUTE_ID } from "@/router/rootShellRouteId";

export type SessionContextValue = {
  givenName: string | undefined;
  postSessionLogout: () => void;
};

export const SessionContext = createContext<SessionContextValue | null>(null);

export const ShellSessionProvider = ({ children }: PropsWithChildren) => {
  const { submitLogout } = useShellActionFetcher();
  const loaderData =
    useRouteLoaderData<ShellBootstrapLoaderData>(ROOT_SHELL_ROUTE_ID);

  const value = useMemo(
    (): SessionContextValue => ({
      givenName: loaderData?.givenName,
      postSessionLogout: submitLogout,
    }),
    [loaderData?.givenName, submitLogout],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
};

export const useSession = (): SessionContextValue => {
  const ctx = use(SessionContext);
  if (ctx == null) {
    throw new Error(
      "useSession must be used within ShellSessionProvider or SessionProviderStub",
    );
  }
  return ctx;
};

/** Session surface for app-level error fallback outside RouterProvider. */
export const getAppLevelErrorFallbackSession = (): SessionContextValue => ({
  givenName: undefined,
  postSessionLogout: () => {
    void postSessionLogout(getPublicRuntimeConfig());
  },
});
